import { useJobsStore } from '@/stores/useJobs';
import { useMixPanelStore } from '@/stores/useMixPanelStore';
import { storeToRefs, defineStore } from 'pinia';

const defineDefaultDistanceState = (type, id, postCode, distance) => {
  const defaultPostalCode = JSON.parse(sessionStorage.getItem('postalCode'));

  return {
    postCode: postCode ? postCode : defaultPostalCode,
    distance: distance ? distance : 0,
    type: type,
    id: id ? id : `${type}-${Date.now()}`, // ensures a unique id even after reloading
    filterType: 'distances',
  };
};

export const useFilterOptions = (id, filterAPI, options) => {
  return defineStore(id, {
    state: () => {
      return {
        location: {
          pickup: {
            distances: [defineDefaultDistanceState('pickup')],
            states: [],
          },

          delivery: {
            distances: [defineDefaultDistanceState('delivery')],
            states: [],
          },
        },
        search: [],
        sort: options.sortObj ? options.sortObj : {},
        previousSort: {
          text: null,
          value: null,
          order: null,
        },
        filters: {
          deliveryPastDue: false,
          pickupPastDue: false,
          deliverToday: false,
          pickupToday: false,
          bolNeeded: false,
        },
      };
    },
    actions: {
      resetAllFilters() {
        this.$reset();
        this.updateMixpanelProperties('clear all filters clicked');
      },
      updateFilters(name, value) {
        this.filters = { ...this.filters, [name]: value };
      },
      updateBulkFilters(filters) {
        this.filters = filters;
      },
      pushNewDistanceRow(type) {
        this.$patch((state) => {
          state.location[type].distances.push(defineDefaultDistanceState(type));
        });
      },
      removeDistanceRow(type, rowIndex) {
        if (rowIndex >= 0) {
          this.location[type].distances.splice(rowIndex, 1);
        }
      },
      resetFiltersByType(type) {
        this.location[type].distances = [defineDefaultDistanceState(type)];
        this.location[type].states = [];
        this.updateMixpanelProperties(`clear all ${type} filters clicked`);
      },
      setDistanceRow(type, filterType, id, formData, $index) {
        this.location[type][filterType][$index] = defineDefaultDistanceState(
          type,
          id,
          formData.postCode,
          formData.distance,
          $index
        );

        this.updateMixpanelProperties(`${type} distance jobs filtered`);
      },
      removeAppliedDistanceFilter(type, id) {
        // remove applied filter from array
        this.location[type].distances = this.location[type].distances.filter(
          (item) => item.id !== id
        );

        this.updateMixpanelProperties(`remove ${type} distance jobs filter`);
      },
      removeAppliedStateFilter(type, id) {
        const stateFilters = this.location[type].states;

        this.location[type].states = stateFilters.filter(
          (item) => item.id !== id
        );
        this.updateMixpanelProperties(`remove ${type} state jobs filter`);
      },
      pushNewSearchQuery(query) {
        // DEV NOTE: When the search feature was originally developed we were intending to pass multiple values.  For MVP we are limiting scope to a single search at a time.
        this.search = [query];

        this.updateMixpanelProperties('jobs searched');
      },
      removeAppliedSearchFilter() {
        this.search = [];
        this.updateMixpanelProperties('jobs searched');
      },
      setStateFilters(type, savedStates) {
        this.location[type].states = savedStates;

        // type === picked up || delivered
        this.updateMixpanelProperties(`${type} state jobs filtered`);
      },
      updateSortParams(sortObj) {
        if (sortObj) {
          this.previousSort = this.sort;
          this.sort = sortObj;
          this.updateMixpanelProperties('job sorted');
        }
      },
      async fetchFilteredJobs(style) {
        const jobsStore = useJobsStore();
        const { jobs, pagination, isPending, currentPageIndex, rowsPerPage } =
          storeToRefs(jobsStore);

        isPending.value = true;

        const body = {
          location: {
            pickup: {
              distances: this.appliedPickupDistanceFilters,
              states: this.appliedPickupStateFilters,
            },
            delivery: {
              distances: this.appliedDeliveryDistanceFilters,
              states: this.appliedDeliveryStateFilters,
            },
          },
          search: this.search.length > 0 ? this.search : null,
          ...this.filters,
        };

        // stringifying the key will ensure that whenever a change is detected (i.e. filter added/removed) a new request will be made - otherwise will pull from cache
        const key = `${JSON.stringify(this.appliedFilters)}--${
          rowsPerPage.value
        }--${this.sort.value}--${this.sort.order}--${currentPageIndex.value}`;

        const { data: fetchedJobs, error } = await useAcvFetch(filterAPI, {
          key: key,
          method: 'POST',
          body,
          initialCache: false, // prevent response from being cached.  revisit for a better caching strategy in the future
          params: {
            page: currentPageIndex.value,
            size: rowsPerPage.value,
            sort: `${[this.sort.value, this.sort.order]}`,
          },
        });

        isPending.value = false;

        if (error.value) {
          return;
        }
        jobs.value =
          style === 'append'
            ? [...jobs.value, ...fetchedJobs.value.data.content]
            : fetchedJobs.value.data.content;
        pagination.value = fetchedJobs.value.data.pagination;
      },
      setToFirstPage() {
        const jobsStore = useJobsStore();
        const { jobs, currentPageIndex } = storeToRefs(jobsStore);

        jobs.value = jobs.value.slice(0, 50);
        currentPageIndex.value = 0;
      },
      updateMixpanelProperties(eventName) {
        const mixPanelStore = useMixPanelStore();
        mixPanelStore.setSearchText(this.searchText);
        mixPanelStore.setSortOptions({
          previousSortByDirection: this.previousSort.order,
          previousSortByField: this.previousSort.text,
          sortByDirection: this.sort.order,
          sortByField: this.sort.text,
        });
        mixPanelStore.setFilterOptions({
          pickupStates: this.appliedPickupStateFilters.length
            ? this.appliedPickupStateFilters
            : null,
          deliveryStates: this.appliedDeliveryStateFilters.length
            ? this.appliedDeliveryStateFilters
            : null,
          pickupZipRanges: this.appliedPickupDistanceFilters.length
            ? this.appliedPickupDistanceFilters
            : null,
          deliveryZipRanges: this.appliedDeliveryDistanceFilters.length
            ? this.appliedDeliveryDistanceFilters
            : null,
        });

        mixPanelStore.fetchMixPanel(eventName);
      },
      resetBulkFiltersAndSort() {
        this.$patch({
          filters: {
            deliveryPastDue: false,
            pickupPastDue: false,
            deliverToday: false,
            pickupToday: false,
            bolNeeded: false,
          },
          sort: options.sortObj,
        });
      },
    },
    getters: {
      appliedDeliveryStateFilters: (state) => [
        ...state.location.delivery.states,
      ],
      appliedPickupStateFilters: (state) => [...state.location.pickup.states],
      appliedPickupDistanceFilters: (state) => [
        ...state.location.pickup.distances.filter(
          (row) => row.distance.length > 0
        ), // filter out empty rows
      ],
      appliedDeliveryDistanceFilters: (state) => [
        ...state.location.delivery.distances.filter(
          (row) => row.distance.length > 0
        ), // filter out empty rows
      ],
      appliedDeliveryFilters: (state) => [
        ...state.appliedDeliveryDistanceFilters,
        ...state.appliedDeliveryStateFilters,
      ],
      appliedPickupFilters: (state) => [
        ...state.appliedPickupDistanceFilters,
        ...state.appliedPickupStateFilters,
      ],
      appliedFilters: (state) => [
        ...state.appliedPickupFilters,
        ...state.appliedDeliveryFilters,
        ...state.search,
      ],
      appliedBulkFilters: (state) => {
        const bulkFilterArray = [];
        for (const prop in state.filters) {
          if (state.filters[prop]) {
            bulkFilterArray.push(true);
          }
        }
        return bulkFilterArray;
      },
      searchText: (state) =>
        state.search[0]?.value ? state.search[0].value : null,
      subFilters: (state) => state.filters,
    },
  });
};
